/**
 * GatewaysConsole — the platform console's Gateways section (docs/plans/2026-10-18_remote-gateway-console.md, P0):
 * every gateway across orgs on the left, the selected one on the right, read only, plus remote diagnostics.
 *
 * - The list: a searchable organisation filter (type part of a name, pick one or more orgs), a site filter inside the
 *   chosen orgs, a text search on the gateway's name, serial or ID, and the online and firmware-line filters, all
 *   applied on the server (GET /v1/admin/gateways). Each row shows the online dot and when the gateway was last seen,
 *   its model as a chip with the firmware version, and the uplink. The list scrolls in its own box.
 * - The details, in tabs that follow the app's screens: Overview, Network (read only), Devices, Diagnostics (health
 *   check, ping, log tail, point read, and the remote ops' history) and Firmware (a link to the firmware console).
 *   A live read is one BLE request carried over the cloud command channel (ADR 0002 A22) through the TypeScript
 *   GatewayLink (lib/iot/gateway-link.ts); it runs when the admin asks for it, and each one shows in the history.
 *
 * The filters, the open gateway and its tab stay in the page URL, so a link opens the same view. The API serves the
 * superadmin console only (rule R8); the org filter only narrows the view. Changes stay on the phone over Bluetooth
 * until P1.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  Cable,
  Check,
  Copy,
  Cpu,
  ExternalLink,
  FileText,
  Gauge,
  Loader2,
  MapPin,
  Monitor,
  Network,
  RefreshCw,
  Router,
  Search,
  Stethoscope,
  Wifi,
  WifiOff,
  X,
  type LucideIcon,
} from "lucide-react";

import {
  adminGatewaysApi,
  type AdminGatewayDetail,
  type AdminGatewayOp,
  type AdminGatewayRow,
} from "@/lib/api/admin-gateways";
import {
  GatewayOpError,
  OP,
  OP_ERROR,
  opErrorOf,
  opErrorWords,
  opLabel,
  remoteGatewayLink,
  type DevicesReport,
  type GatewayLink,
  type GatewayNetDoc,
  type GatewayNetIface,
  type GatewaySavedWifi,
  type GatewayStatusDoc,
  type HealthReport,
  type LogTail,
  type PingResult,
  type PointReadResult,
} from "@/lib/iot/gateway-link";
import { readBirth } from "@/lib/iot/gateway-birth";
import {
  DETAIL_TABS,
  LINE_OPTIONS,
  agoWords,
  lineLabel,
  lineModel,
  listQuery,
  matchOrgs,
  opGate,
  presenceOf,
  readConsoleState,
  sharedPlace,
  shortAgo,
  sitesOf,
  splitVersion,
  toggleId,
  uplinkKindWord,
  uplinkWords,
  writeConsoleState,
  type DetailTab,
  type GatewayConsoleState,
  type OrgOption,
  type SiteOption,
} from "@/lib/gateway-console";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/lib/ui/tabs";
import { cn } from "@/lib/ui/cn";

const PRESENCE_DOT = { online: "bg-success", offline: "bg-danger", unclaimed: "bg-on-surface-muted/40" } as const;
const PRESENCE_WORDS = { online: "Online", offline: "Offline", unclaimed: "Not claimed" } as const;

const TAB_LABELS: Record<DetailTab, string> = {
  overview: "Overview",
  network: "Network",
  devices: "Devices",
  diagnostics: "Diagnostics",
  firmware: "Firmware",
};

// ── The section ─────────────────────────────────────────────────────────────

export function GatewaysConsole({ orgs }: { orgs: OrgOption[] }) {
  const [params, setParams] = useSearchParams();
  const state = readConsoleState(params);
  // A change is worked out from the URL as it is now, not as this render saw it: React Router renders a URL change as
  // a transition, so a second click can come before the render of the first (two orgs picked quickly), and its
  // searchParams (also the one a function given to setSearchParams gets) is still the render's. The browser's URL
  // changes at once.
  const update = useCallback(
    (change: Partial<GatewayConsoleState> | ((s: GatewayConsoleState) => Partial<GatewayConsoleState>)) => {
      const now = new URLSearchParams(window.location.search);
      const cur = readConsoleState(now);
      setParams(writeConsoleState(now, { ...cur, ...(typeof change === "function" ? change(cur) : change) }), {
        replace: true,
      });
    },
    [setParams],
  );

  // The search box keeps what is typed; the URL, and with it the server query, follows once typing stops.
  const [qText, setQText] = useState(() => state.q);
  useEffect(() => {
    const t = setTimeout(() => {
      if (readConsoleState(new URLSearchParams(window.location.search)).q !== qText) update({ q: qText });
    }, 300);
    return () => clearTimeout(t);
  }, [qText, update]);

  const query = listQuery(state);
  const listQ = useInfiniteQuery({
    queryKey: ["admin-gateways", query],
    queryFn: ({ pageParam }) => adminGatewaysApi.list({ ...query, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    refetchInterval: 30_000,
  });
  const rows = useMemo(() => listQ.data?.pages.flatMap((p) => p.items) ?? [], [listQ.data]);
  const total = listQ.data?.pages[0]?.total ?? 0;

  // The site filter offers the sites that hold gateways in the chosen orgs.
  const sitesQ = useQuery({
    queryKey: ["admin-gateways-sites", state.orgs],
    queryFn: () => adminGatewaysApi.list({ orgIds: state.orgs, limit: 200 }),
    enabled: state.orgs.length > 0,
  });
  const siteOptions = useMemo(() => sitesOf(sitesQ.data?.items ?? []), [sitesQ.data]);

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-base font-semibold text-on-surface">Gateways</h2>
        <p className="text-xs text-on-surface-muted">
          Every gateway across organisations. Pick one to read its status, adapters and devices, and to run a
          health check, a ping, a log tail or a point read over its cloud link. Read only: changes stay on the
          phone over Bluetooth.
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-[minmax(320px,2fr)_3fr]">
        {/* On a wide screen the filters and the list stay in view while the details scroll: the column sticks to the
            top of the page's scroll area (main, below the 3.5rem header) and fits its height, and the list scrolls
            inside its own box. */}
        <div className="flex min-w-0 flex-col gap-3 lg:sticky lg:top-4 lg:max-h-[calc(100vh-5.5rem)] lg:self-start">
          <OrgFilter
            orgs={orgs}
            chosen={state.orgs}
            onToggle={(id) => update((s) => ({ orgs: toggleId(s.orgs, id), sites: [] }))}
            onClear={() => update({ orgs: [], sites: [] })}
          />
          <div className="grid grid-cols-2 gap-2">
            <div className="relative col-span-2">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-on-surface-muted" />
              <Input
                aria-label="Search gateways"
                value={qText}
                onChange={(e) => setQText(e.target.value)}
                placeholder="Name, serial (SGWP-7E1A90A1) or ID"
                className="pl-8"
                maxLength={100}
              />
            </div>
            <SiteFilter
              sites={siteOptions}
              chosen={state.sites[0] ?? ""}
              disabled={state.orgs.length === 0}
              onChange={(id) => update({ sites: id ? [id] : [] })}
            />
            <NativeSelect
              aria-label="Online"
              value={state.online}
              onChange={(e) => update({ online: e.target.value as GatewayConsoleState["online"] })}
            >
              <option value="">Online and offline</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </NativeSelect>
            <NativeSelect
              aria-label="Firmware line"
              value={state.line}
              onChange={(e) => update({ line: e.target.value })}
              className="col-span-2"
            >
              <option value="">Every firmware line</option>
              {LINE_OPTIONS.map((l) => (
                <option key={l} value={l}>
                  {lineLabel(l)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <GatewayListView
            rows={rows}
            total={total}
            selectedId={state.gw}
            loading={listQ.isLoading}
            error={listQ.error ? "The gateway list did not load. Try again." : null}
            hasMore={!!listQ.hasNextPage}
            loadingMore={listQ.isFetchingNextPage}
            onMore={() => void listQ.fetchNextPage()}
            onSelect={(id) => update({ gw: id, view: "overview" })}
          />
        </div>
        <div className="min-w-0">
          {state.gw ? (
            <GatewayDetailPane
              key={state.gw}
              id={state.gw}
              view={state.view}
              onView={(v) => update({ view: v })}
              onClose={() => update({ gw: null, view: "overview" })}
            />
          ) : (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-on-surface-muted">
              Pick a gateway on the left.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ── The organisation and site filters ──────────────────────────────────────

/** The searchable organisation filter: type part of a name, tick one or more orgs. */
export function OrgFilter({
  orgs,
  chosen,
  onToggle,
  onClear,
}: {
  orgs: OrgOption[];
  chosen: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <OrgFilterView
      orgs={orgs}
      chosen={chosen}
      query={query}
      open={open}
      onQuery={(q) => {
        setQuery(q);
        setOpen(true);
      }}
      onOpen={setOpen}
      onToggle={onToggle}
      onClear={onClear}
    />
  );
}

export function OrgFilterView({
  orgs,
  chosen,
  query,
  open,
  onQuery,
  onOpen,
  onToggle,
  onClear,
}: {
  orgs: OrgOption[];
  chosen: string[];
  query: string;
  open: boolean;
  onQuery: (q: string) => void;
  onOpen: (open: boolean) => void;
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  const byId = new Map(orgs.map((o) => [o.id, o]));
  const matches = matchOrgs(orgs, query, chosen);
  return (
    <div
      className="flex flex-col gap-2"
      data-testid="org-filter"
      onBlur={(e) => {
        // Focus left the filter (a click on the list, the search box…): the list closes.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onOpen(false);
      }}
    >
      <div className="relative">
        <Input
          role="combobox"
          aria-expanded={open}
          aria-controls="gateway-org-options"
          aria-label="Organisations"
          value={query}
          placeholder={chosen.length > 0 ? "Add an organisation" : "Every organisation: type to pick"}
          onChange={(e) => onQuery(e.target.value)}
          onFocus={() => onOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onOpen(false);
          }}
        />
        {open && (
          <div
            id="gateway-org-options"
            role="listbox"
            aria-multiselectable="true"
            tabIndex={-1}
            onKeyDown={(e) => {
              if (e.key === "Escape") onOpen(false);
            }}
            className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-surface-elevated p-1 shadow-lg"
          >
            {matches.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-on-surface-muted">No organisation matches “{query.trim()}”.</p>
            ) : (
              matches.map((o) => {
                const on = chosen.includes(o.id);
                return (
                  <button
                    key={o.id}
                    type="button"
                    role="option"
                    aria-label={`${o.name} (${o.slug})`}
                    aria-selected={on}
                    onClick={() => onToggle(o.id)}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-surface-muted"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "flex size-4 items-center justify-center rounded border",
                        on ? "border-brand bg-brand text-brand-foreground" : "border-border",
                      )}
                    >
                      {on && <Check className="size-3" />}
                    </span>
                    <span className="truncate text-on-surface">{o.name}</span>
                    <span className="ml-auto truncate text-xs text-on-surface-muted">{o.slug}</span>
                  </button>
                );
              })
            )}
            <div className="mt-1 border-t border-border pt-1 text-right">
              <Button size="sm" variant="ghost" onClick={() => onOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        )}
      </div>
      {chosen.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {chosen.map((id) => (
            <span
              key={id}
              className="inline-flex items-center gap-1 rounded-md border border-border bg-surface-muted px-2 py-0.5 text-xs text-on-surface"
            >
              {byId.get(id)?.name ?? id}
              <button
                type="button"
                aria-label={`Remove ${byId.get(id)?.name ?? id}`}
                onClick={() => onToggle(id)}
                className="text-on-surface-muted hover:text-on-surface"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
          <button type="button" onClick={onClear} className="text-xs text-brand hover:underline">
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

function SiteFilter({
  sites,
  chosen,
  disabled,
  onChange,
}: {
  sites: SiteOption[];
  chosen: string;
  disabled: boolean;
  onChange: (id: string) => void;
}) {
  return (
    <NativeSelect
      aria-label="Site"
      value={chosen}
      disabled={disabled}
      title={disabled ? "Pick an organisation first" : undefined}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{disabled ? "Every site" : "Every site in these orgs"}</option>
      {sites.map((s) => (
        <option key={s.id} value={s.id}>
          {s.name} · {s.orgName}
        </option>
      ))}
    </NativeSelect>
  );
}

// ── The list ────────────────────────────────────────────────────────────────

export function GatewayListView({
  rows,
  total,
  selectedId,
  loading,
  error,
  hasMore,
  loadingMore,
  onMore,
  onSelect,
  now,
}: {
  rows: AdminGatewayRow[];
  total: number;
  selectedId: string | null;
  loading?: boolean;
  error?: string | null;
  hasMore?: boolean;
  loadingMore?: boolean;
  onMore?: () => void;
  onSelect: (id: string) => void;
  now?: Date;
}) {
  // A link that opens a gateway (?gw=) can land on a row below the box's fold: bring that row into view once. A click
  // picks a row already in view, so nothing moves then.
  const listRef = useRef<HTMLUListElement>(null);
  const hasSelected = selectedId !== null && rows.some((g) => g.id === selectedId);
  useEffect(() => {
    if (hasSelected) listRef.current?.querySelector('[aria-current="true"]')?.scrollIntoView({ block: "nearest" });
  }, [selectedId, hasSelected]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-on-surface-muted">
        <Loader2 className="size-4 animate-spin" /> Loading gateways…
      </div>
    );
  }
  if (error) {
    return <p className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{error}</p>;
  }
  if (rows.length === 0) {
    return <p className="py-6 text-center text-sm text-on-surface-muted">No gateway matches these filters.</p>;
  }
  // When every row is in one org and site, the count line names it once and the rows leave it out.
  const place = sharedPlace(rows);
  const online = rows.filter((g) => presenceOf(g) === "online").length;
  return (
    <div className="flex min-h-0 flex-col gap-2">
      <p className="flex flex-wrap gap-x-1.5 text-xs text-on-surface-muted">
        <span>
          {hasMore ? `${rows.length} of ${total} gateways` : `${total} gateway${total === 1 ? "" : "s"} · ${online} online`}
        </span>
        {place && (
          <span className="min-w-0 truncate">
            · all in {place.orgName} · {place.siteName}
          </span>
        )}
      </p>
      {/* The list scrolls in its own box (on a wide screen the box fills the column, which fits the screen). The box is
          relative so that the rows' sr-only words (position: absolute) stay inside it: else they take main as their
          containing block and stretch the page's scroll down to where the last row would be. */}
      <div
        data-testid="gateway-list-scroll"
        className="relative max-h-[60vh] min-h-0 overflow-y-auto rounded-lg border border-border lg:max-h-none"
      >
        <ul ref={listRef} className="flex flex-col divide-y divide-border" aria-label="Gateways">
          {rows.map((g) => (
            <GatewayRow key={g.id} g={g} selected={g.id === selectedId} showPlace={!place} onSelect={onSelect} now={now} />
          ))}
        </ul>
        {hasMore && (
          <div className="border-t border-border p-2">
            <Button variant="secondary" size="sm" className="w-full" onClick={onMore} disabled={loadingMore}>
              {loadingMore && <Loader2 className="animate-spin" />} Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One gateway in the list, in three lines: the dot, name and when it was last seen; the model chip and the firmware
 * version; the uplink, the org and site, and the BLE name.
 */
function GatewayRow({
  g,
  selected,
  showPlace,
  onSelect,
  now,
}: {
  g: AdminGatewayRow;
  selected: boolean;
  showPlace: boolean;
  onSelect: (id: string) => void;
  now?: Date;
}) {
  const p = presenceOf(g);
  return (
    <li>
      <button
        type="button"
        aria-current={selected ? "true" : undefined}
        onClick={() => onSelect(g.id)}
        className={cn(
          "grid w-full grid-cols-[0.5rem_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-1 px-3 py-2.5 text-left transition-colors hover:bg-surface-muted",
          selected && "bg-brand-muted/40 hover:bg-brand-muted/50",
        )}
      >
        <span
          aria-hidden
          data-presence={p}
          className={cn("size-2 rounded-full", PRESENCE_DOT[p])}
          title={PRESENCE_WORDS[p]}
        />
        <span className="truncate text-sm font-medium text-on-surface">
          {g.name}
          <span className="sr-only">, {PRESENCE_WORDS[p]}</span>
        </span>
        <span
          className={cn("text-[11px] tabular-nums", p === "offline" ? "text-danger" : "text-on-surface-muted")}
          title={`Last seen ${agoWords(g.last_seen, now)}`}
        >
          {shortAgo(g.last_seen, now)}
        </span>

        <span className="col-span-2 col-start-2 flex min-w-0 items-center gap-2">
          <ModelChip line={g.firmware_line} />
          {g.firmware && <VersionText version={g.firmware} />}
        </span>

        <span className="col-start-2 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-on-surface-muted">
          <UplinkText uplink={g.uplink} />
          {showPlace && (
            <span className="inline-flex min-w-0 items-center gap-1">
              <MapPin aria-hidden className="size-3 shrink-0" />
              <span className="truncate">
                {g.org_name} · {g.site_name}
              </span>
            </span>
          )}
        </span>
        {g.ble_name && (
          <span className="col-start-3 justify-self-end font-mono text-[11px] text-on-surface-muted">{g.ble_name}</span>
        )}
      </button>
    </li>
  );
}

/** The model chip's colour and icon for each firmware line; never a status colour, since the dot says that. */
const MODEL_CHIP: Record<string, { tone: string; icon: LucideIcon }> = {
  sgwp: { tone: "border-transparent bg-accent-muted text-accent", icon: Router },
  sgwmp: { tone: "border-transparent bg-brand-muted text-brand", icon: Monitor },
  sgwe: { tone: "border-border bg-surface-muted text-on-surface", icon: Cpu },
};

/** The gateway's model as a chip: the line's code and its hardware name (the full name in the tooltip). */
export function ModelChip({ line }: { line: string | null }) {
  const m = lineModel(line);
  const chip = m ? MODEL_CHIP[m.key] : undefined;
  const Icon = chip?.icon;
  return (
    <Badge
      variant="outline"
      data-line={m?.key ?? "none"}
      title={lineLabel(line)}
      className={cn("shrink-0 gap-1 px-1.5 text-[11px]", chip?.tone ?? "border-dashed text-on-surface-muted")}
    >
      {Icon && <Icon aria-hidden className="size-3" />}
      {m ? (
        <>
          <span className="font-mono font-semibold">{m.code}</span>
          {m.model && <span>{m.model}</span>}
        </>
      ) : (
        "No line"
      )}
    </Badge>
  );
}

/** "v0.1.3" with a pre-release or build part ("-dev.2655a24") dimmed after it; the whole version in the tooltip. */
function VersionText({ version }: { version: string }) {
  const [release, rest] = splitVersion(version);
  return (
    <span className="min-w-0 truncate font-mono text-[11px] text-on-surface" title={`v${version}`}>
      v{release}
      {rest && <span className="text-on-surface-muted">{rest}</span>}
    </span>
  );
}

/** The uplink: an icon for its kind, then the Wi-Fi network's name and the interface. */
function UplinkText({ uplink: u }: { uplink: AdminGatewayRow["uplink"] }) {
  if (!u) {
    return (
      <span className="inline-flex items-center gap-1">
        <WifiOff aria-hidden className="size-3 shrink-0" />
        No uplink reported
      </span>
    );
  }
  const Icon = u.kind === "wifi" ? Wifi : u.kind === "ethernet" ? Cable : Network;
  const ssid = u.kind === "wifi" ? u.ssid : null;
  return (
    <span className="inline-flex min-w-0 items-center gap-1" title={uplinkWords(u)}>
      <Icon aria-hidden className="size-3 shrink-0" />
      <span className="sr-only">{uplinkKindWord(u)} </span>
      {ssid && <span className="truncate font-medium text-on-surface">{ssid}</span>}
      <span className="shrink-0 font-mono text-[11px]">{u.iface}</span>
    </span>
  );
}

// ── The details ─────────────────────────────────────────────────────────────

/** One live read: its answer, its error, and whether it runs. */
interface Live<T> {
  run: () => void;
  data: T | undefined;
  error: GatewayOpError | null;
  pending: boolean;
}

function useLive<T>(id: string, opcode: number, call: () => Promise<T>): Live<T> {
  const qc = useQueryClient();
  const m = useMutation<T, GatewayOpError>({
    mutationFn: async () => {
      try {
        return await call();
      } catch (err) {
        throw opErrorOf(err, opcode);
      }
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: ["admin-gateway-ops", id] }),
  });
  return { run: () => m.mutate(), data: m.data, error: m.error ?? null, pending: m.isPending };
}

function GatewayDetailPane({
  id,
  view,
  onView,
  onClose,
}: {
  id: string;
  view: DetailTab;
  onView: (v: DetailTab) => void;
  onClose: () => void;
}) {
  const detailQ = useQuery({
    queryKey: ["admin-gateway", id],
    queryFn: () => adminGatewaysApi.get(id),
    refetchInterval: 30_000,
  });
  const link = useMemo(() => remoteGatewayLink(id), [id]);
  if (detailQ.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-on-surface-muted">
        <Loader2 className="size-4 animate-spin" /> Loading the gateway…
      </div>
    );
  }
  if (!detailQ.data) {
    return (
      <p className="rounded-md border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
        The gateway did not load. It may have been removed.{" "}
        <button type="button" className="underline" onClick={onClose}>
          Close
        </button>
      </p>
    );
  }
  return <GatewayDetail detail={detailQ.data} link={link} view={view} onView={onView} onClose={onClose} />;
}

function GatewayDetail({
  detail,
  link,
  view,
  onView,
  onClose,
}: {
  detail: AdminGatewayDetail;
  link: GatewayLink;
  view: DetailTab;
  onView: (v: DetailTab) => void;
  onClose: () => void;
}) {
  const id = detail.id;
  const status = useLive<GatewayStatusDoc>(id, OP.STATUS_GET, () => link.fetchStatus());
  const net = useLive<GatewayNetDoc>(id, OP.NET_GET, () => link.netGet());
  const wifi = useLive<GatewaySavedWifi[]>(id, OP.WIFI_LIST, () => link.wifiList());
  const devices = useLive<DevicesReport>(id, OP.DEVICES_GET, () => link.devicesGet());
  const health = useLive<HealthReport>(id, OP.HEALTH_CHECK, () => link.healthCheck());
  const [pingReq, setPingReq] = useState({ iface: "", target: "gateway", ip: "", count: 3 });
  const ping = useLive<PingResult>(id, OP.PING, () =>
    link.ping({
      iface: pingReq.iface,
      target: pingReq.target === "ip" ? pingReq.ip.trim() : pingReq.target,
      count: pingReq.count,
    }),
  );
  const [logReq, setLogReq] = useState({ lines: 100, level: "", grep: "" });
  const logs = useLive<LogTail>(id, OP.LOG_TAIL, () =>
    link.logTail({
      lines: logReq.lines,
      ...(logReq.level ? { level: logReq.level } : {}),
      ...(logReq.grep.trim() ? { grep: logReq.grep.trim() } : {}),
    }),
  );
  const [pointReq, setPointReq] = useState({ device: "", metric: "" });
  const point = useLive<PointReadResult>(id, OP.POINT_READ, () =>
    link.pointRead({ device: pointReq.device.trim(), metric: pointReq.metric.trim() }),
  );

  const historyQ = useQuery({
    queryKey: ["admin-gateway-ops", id],
    queryFn: () => adminGatewaysApi.ops(id, null, 20),
    enabled: view === "diagnostics",
  });

  const allowed = new Set(detail.allowed_ops.map((o) => o.opcode));
  const birth = readBirth(detail.capabilities);
  const ifaces = (net.data?.ifaces.map((n) => n.name) ?? birth.networks?.map((n) => n.iface) ?? []).filter(Boolean);
  const gate = (opcode: number) => opGate(detail, allowed, opcode);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border p-4">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className={cn("mt-1.5 size-2.5 shrink-0 rounded-full", PRESENCE_DOT[presenceOf(detail)])}
        />
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-on-surface">{detail.name}</h3>
          <p className="truncate text-xs text-on-surface-muted">
            {PRESENCE_WORDS[presenceOf(detail)]} · {detail.org_name} · {detail.site_name}
            {detail.ble_name ? ` · ${detail.ble_name}` : ""}
          </p>
        </div>
        <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
          <X />
        </Button>
      </div>
      <OfflineNotice detail={detail} />
      <Tabs value={view} onValueChange={(v) => onView(v as DetailTab)}>
        <TabsList aria-label="Gateway">
          {DETAIL_TABS.map((t) => (
            <TabsTrigger key={t} value={t}>
              {TAB_LABELS[t]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="pt-3">
          <OverviewView detail={detail} />
          <LiveBlock
            title="Live status (STATUS_GET)"
            gate={gate(OP.STATUS_GET)}
            live={status}
            render={(s) => <StatusView status={s} />}
          />
        </TabsContent>
        <TabsContent value="network" className="pt-3">
          <BirthNetworksView detail={detail} />
          <LiveBlock
            title="Adapters now (NET_GET)"
            gate={gate(OP.NET_GET)}
            live={net}
            render={(n) => <NetIfacesView ifaces={n.ifaces} control={n.control} />}
          />
          <LiveBlock
            title="Saved Wi-Fi networks (WIFI_LIST)"
            gate={gate(OP.WIFI_LIST)}
            live={wifi}
            render={(w) => <SavedWifiView networks={w} />}
          />
        </TabsContent>
        <TabsContent value="devices" className="pt-3">
          <CloudDevicesView detail={detail} />
          <LiveBlock
            title="Device reads on the gateway (DEVICES_GET)"
            gate={gate(OP.DEVICES_GET)}
            live={devices}
            render={(d) => <DevicesView report={d} />}
          />
        </TabsContent>
        <TabsContent value="diagnostics" className="flex flex-col pt-3">
          <LiveBlock
            title="Health check (HEALTH_CHECK)"
            gate={gate(OP.HEALTH_CHECK)}
            live={health}
            render={(h) => <HealthView report={h} />}
            hint="About 8 s on the gateway."
          />
          <LiveBlock
            title="Ping (PING)"
            gate={gate(OP.PING)}
            live={ping}
            runDisabled={!pingReq.iface || (pingReq.target === "ip" && !pingReq.ip.trim())}
            form={
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <NativeSelect
                  aria-label="Interface"
                  value={pingReq.iface}
                  onChange={(e) => setPingReq({ ...pingReq, iface: e.target.value })}
                >
                  <option value="">Interface…</option>
                  {ifaces.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </NativeSelect>
                <NativeSelect
                  aria-label="Target"
                  value={pingReq.target}
                  onChange={(e) => setPingReq({ ...pingReq, target: e.target.value })}
                >
                  <option value="gateway">Its router</option>
                  <option value="dns">Its DNS server</option>
                  <option value="broker">The broker</option>
                  <option value="ip">An IPv4 address</option>
                </NativeSelect>
                {pingReq.target === "ip" && (
                  <Input
                    aria-label="IPv4 address"
                    value={pingReq.ip}
                    placeholder="192.168.0.10"
                    onChange={(e) => setPingReq({ ...pingReq, ip: e.target.value })}
                  />
                )}
                <NativeSelect
                  aria-label="Count"
                  value={String(pingReq.count)}
                  onChange={(e) => setPingReq({ ...pingReq, count: Number(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <option key={n} value={n}>
                      {n} probe{n === 1 ? "" : "s"}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            }
            render={(p) => <PingView result={p} />}
          />
          <LiveBlock
            title="Log tail (LOG_TAIL)"
            gate={gate(OP.LOG_TAIL)}
            live={logs}
            form={
              <div className="grid grid-cols-3 gap-2">
                <NativeSelect
                  aria-label="Lines"
                  value={String(logReq.lines)}
                  onChange={(e) => setLogReq({ ...logReq, lines: Number(e.target.value) })}
                >
                  {[50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n} lines
                    </option>
                  ))}
                </NativeSelect>
                <NativeSelect
                  aria-label="Level"
                  value={logReq.level}
                  onChange={(e) => setLogReq({ ...logReq, level: e.target.value })}
                >
                  <option value="">Every level</option>
                  <option value="warn">Warnings and errors</option>
                  <option value="error">Errors</option>
                </NativeSelect>
                <Input
                  aria-label="Contains"
                  value={logReq.grep}
                  placeholder="Contains…"
                  onChange={(e) => setLogReq({ ...logReq, grep: e.target.value })}
                />
              </div>
            }
            render={(l) => <LogTailView tail={l} />}
          />
          <LiveBlock
            title="Point read (POINT_READ)"
            gate={gate(OP.POINT_READ)}
            live={point}
            runDisabled={!pointReq.device.trim() || !pointReq.metric.trim()}
            hint="One read of one configured point, as the poller reads it."
            form={
              <div className="grid grid-cols-2 gap-2">
                <Input
                  aria-label="Device"
                  list="gateway-device-names"
                  value={pointReq.device}
                  placeholder="Device name"
                  onChange={(e) => setPointReq({ ...pointReq, device: e.target.value })}
                />
                <datalist id="gateway-device-names">
                  {(devices.data?.devices ?? []).map((d) => (
                    <option key={d.name} value={d.name} />
                  ))}
                </datalist>
                <Input
                  aria-label="Metric"
                  value={pointReq.metric}
                  placeholder="Metric"
                  onChange={(e) => setPointReq({ ...pointReq, metric: e.target.value })}
                />
              </div>
            }
            render={(p) => <PointValueView result={p} />}
          />
          <div className="mt-2 flex flex-col gap-2">
            <h4 className="text-sm font-medium text-on-surface">Remote ops on this gateway</h4>
            {historyQ.isLoading ? (
              <Loader2 className="size-4 animate-spin text-on-surface-muted" />
            ) : (
              <OpHistoryView ops={historyQ.data?.items ?? []} />
            )}
          </div>
        </TabsContent>
        <TabsContent value="firmware" className="pt-3">
          <FirmwareView detail={detail} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/** A gateway the console cannot reach now. */
export function OfflineNotice({ detail }: { detail: Pick<AdminGatewayDetail, "online" | "claimed" | "online_changed_at" | "remote_ops"> }) {
  if (detail.claimed && detail.online && detail.remote_ops) return null;
  const words = !detail.claimed
    ? "The gateway is not claimed yet, so it takes no commands."
    : !detail.online
      ? `The gateway is offline (since ${agoWords(detail.online_changed_at)}). Remote requests are not sent and never queued.`
      : "This gateway's firmware takes no remote requests. It needs a firmware with remote ops.";
  return (
    <p className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2.5 text-xs text-on-surface" role="status">
      <WifiOff className="mt-0.5 size-3.5 shrink-0 text-warning" />
      {words}
    </p>
  );
}

/** A live read: its button, its form, and its answer or error. */
function LiveBlock<T>({
  title,
  gate,
  live,
  render,
  form,
  hint,
  runDisabled,
}: {
  title: string;
  gate: { ok: boolean; why?: string };
  live: Live<T>;
  render: (data: T) => ReactNode;
  form?: ReactNode;
  hint?: string;
  runDisabled?: boolean;
}) {
  return (
    <div className="mt-3 flex flex-col gap-2 rounded-md border border-border p-3">
      <div className="flex items-center gap-2">
        <h4 className="flex-1 text-sm font-medium text-on-surface">{title}</h4>
        <Button
          size="sm"
          variant="secondary"
          disabled={!gate.ok || live.pending || runDisabled}
          title={gate.why}
          onClick={live.run}
        >
          {live.pending ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          {live.data === undefined ? "Read" : "Read again"}
        </Button>
      </div>
      {hint && <p className="text-xs text-on-surface-muted">{hint}</p>}
      {!gate.ok && gate.why && <p className="text-xs text-on-surface-muted">{gate.why}</p>}
      {form}
      {live.error && <OpErrorNotice error={live.error} />}
      {live.data !== undefined && !live.error && render(live.data)}
    </div>
  );
}

/** A request with no answer, in words. */
export function OpErrorNotice({ error }: { error: GatewayOpError }) {
  const tone = error.kind === "gateway_error" || error.kind === "rejected" ? "warning" : "danger";
  return (
    <p
      role="alert"
      data-kind={error.kind}
      className={cn(
        "rounded-md border p-2 text-xs",
        tone === "warning" ? "border-warning/40 bg-warning/10 text-on-surface" : "border-danger/40 bg-danger/10 text-danger",
      )}
    >
      {opErrorWords(error)}
    </p>
  );
}

// ── The tabs' views (pure) ──────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] uppercase tracking-wide text-on-surface-muted">{label}</dt>
      <dd className="text-sm text-on-surface">{children}</dd>
    </div>
  );
}

export function OverviewView({ detail, now }: { detail: AdminGatewayDetail; now?: Date }) {
  const birth = readBirth(detail.capabilities);
  return (
    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Field label="Connection">
        {PRESENCE_WORDS[presenceOf(detail)]}
        {detail.online_changed_at && (
          <span className="text-on-surface-muted"> · since {agoWords(detail.online_changed_at, now)}</span>
        )}
      </Field>
      <Field label="Last seen">{agoWords(detail.last_seen, now)}</Field>
      <Field label="Uplink">{uplinkWords(detail.uplink)}</Field>
      <Field label="Last birth">{agoWords(detail.birth_at, now)}</Field>
      <Field label="Firmware">
        {detail.firmware ? `v${detail.firmware}` : "Not reported"} · {lineLabel(detail.firmware_line)}
      </Field>
      <Field label="Host">{birth.host ?? "Not reported"}</Field>
      <Field label="BLE name">{detail.ble_name ?? "Not reported"}</Field>
      <Field label="Gateway ID">
        <span className="font-mono text-xs">{detail.id}</span>
      </Field>
      <Field label="Organisation · site">
        {detail.org_name} · {detail.site_name}
      </Field>
      <Field label="Certificate">
        {detail.claimed ? `Until ${detail.cert_expires_at ? new Date(detail.cert_expires_at).toLocaleDateString() : "?"}` : "None (not claimed)"}
      </Field>
      <Field label="Drivers">
        {birth.drivers.length > 0 ? birth.drivers.map((d) => (d.v ? `${d.id} ${d.v}` : d.id)).join(", ") : "None reported"}
      </Field>
      <Field label="Remote requests">
        {detail.allowed_ops.length > 0 ? `${detail.allowed_ops.length} reads` : "None"}
      </Field>
    </dl>
  );
}

export function StatusView({ status }: { status: GatewayStatusDoc }) {
  return (
    <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      <Field label="State">{status.state}</Field>
      <Field label="Stage">{status.label || status.stage || "—"}</Field>
      <Field label="Link">{[status.link, status.iface, status.ip].filter(Boolean).join(" · ") || "—"}</Field>
      {status.error && (
        <Field label="Error">
          {status.error}
          {status.detail ? `: ${status.detail}` : ""}
        </Field>
      )}
      {status.net_pending && <Field label="Change waiting">{status.net_pending.iface ?? "yes"}</Field>}
      <Field label="Linked once">{status.linked_once ? "Yes" : "No"}</Field>
    </dl>
  );
}

const th = "px-2 py-1.5 text-left font-medium";
const td = "px-2 py-1.5 align-top";

function Table({ head, children }: { head: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-xs">
        <thead className="bg-surface-elevated text-[11px] uppercase tracking-wide text-on-surface-muted">
          <tr>
            {head.map((h) => (
              <th key={h} className={th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function BirthNetworksView({ detail }: { detail: Pick<AdminGatewayDetail, "capabilities" | "birth_at"> }) {
  const nets = readBirth(detail.capabilities).networks;
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-on-surface-muted">
        <Network className="mr-1 inline size-3.5" />
        Adapters in the last birth ({agoWords(detail.birth_at)}). Read only: network changes stay on the phone over
        Bluetooth.
      </p>
      {!nets || nets.length === 0 ? (
        <p className="text-xs text-on-surface-muted">The birth lists no adapters.</p>
      ) : (
        <Table head={["Interface", "Kind", "Addresses", "Router", "Internet", "Wi-Fi"]}>
          {nets.map((n) => (
            <tr key={n.iface} className="border-t border-border">
              <td className={cn(td, "font-medium")}>
                {n.iface} {!n.up && <Badge variant="secondary">down</Badge>}
              </td>
              <td className={td}>{n.kind}</td>
              <td className={cn(td, "font-mono")}>{n.cidrs.join(", ") || "—"}</td>
              <td className={cn(td, "font-mono")}>{n.gateway ?? "—"}</td>
              <td className={td}>{n.default_route ? <Badge variant="success">carries it</Badge> : "—"}</td>
              <td className={td}>{n.ssid ?? "—"}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

/**
 * The MAC to give plant IT (A27.6): the permanent one when the gateway knows it, else the current one. `now` is the
 * current MAC when it differs (NetworkManager can randomise it), so the two are never mixed up.
 */
function macOf(n: Pick<GatewayNetIface, "mac" | "perm_mac">): { shown: string; now: string } {
  const perm = n.perm_mac ?? "";
  const shown = perm || n.mac;
  return { shown, now: perm && n.mac && n.mac.toLowerCase() !== perm.toLowerCase() ? n.mac : "" };
}

/** The copy button of one MAC. `onCopied` runs only when the browser let the write through. */
export function MacCopyButton({
  iface,
  value,
  copied,
  onCopied,
}: {
  iface: string;
  value: string;
  copied: boolean;
  onCopied: () => void;
}) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      onCopied();
    } catch {
      /* clipboard permission: the value stays on screen to select */
    }
  };
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-6 gap-1 px-1 text-[11px] text-on-surface-muted [&_svg]:size-3.5"
      aria-label={`Copy ${iface} MAC`}
      onClick={() => void copy()}
    >
      {copied ? (
        <>
          <Check className="text-success" />
          Copied
        </>
      ) : (
        <Copy />
      )}
    </Button>
  );
}

function MacCell({ iface, mac, perm_mac }: { iface: string } & Pick<GatewayNetIface, "mac" | "perm_mac">) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(t);
  }, [copied]);
  const { shown, now } = macOf({ mac, perm_mac });
  if (!shown) return <>—</>;
  return (
    <>
      <span className="flex items-center gap-1 font-mono">
        {shown}
        <MacCopyButton iface={iface} value={shown} copied={copied} onCopied={() => setCopied(true)} />
      </span>
      <span role="status" className="sr-only">
        {copied ? `${iface} MAC copied` : ""}
      </span>
      {now && <span className="block font-mono text-[11px] text-on-surface-muted">{`now ${now}`}</span>}
    </>
  );
}

export function NetIfacesView({ ifaces, control }: { ifaces: GatewayNetIface[]; control?: GatewayNetDoc["control"] }) {
  return (
    <div className="flex flex-col gap-2">
      {(control === "auth" || control === "no") && (
        <p className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2.5 text-xs text-on-surface" role="status">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" />
          This gateway may not change its network: the NetworkManager rule for its user is missing.
        </p>
      )}
      {ifaces.length === 0 ? <p className="text-xs text-on-surface-muted">No adapters.</p> : <NetIfacesTable ifaces={ifaces} />}
    </div>
  );
}

function NetIfacesTable({ ifaces }: { ifaces: GatewayNetIface[] }) {
  return (
    <Table head={["Interface", "MAC", "Link", "Method", "Addresses", "Router", "DNS", "Internet"]}>
      {ifaces.map((n) => (
        <tr key={n.name} className="border-t border-border">
          <td className={cn(td, "font-medium")}>
            {n.name}
            <span className="block text-[11px] font-normal text-on-surface-muted">
              {n.kind}
              {n.bus ? ` · ${n.bus}` : ""}
            </span>
          </td>
          <td className={td}>
            <MacCell iface={n.name} mac={n.mac} perm_mac={n.perm_mac} />
          </td>
          <td className={td}>
            {n.enabled === false ? "Disabled" : !n.carrier && n.kind === "ethernet" ? "No cable" : n.up ? "Up" : "Down"}
            {n.pending && <Badge variant="warning" className="ml-1">change waiting</Badge>}
          </td>
          <td className={td}>{n.method || "—"}</td>
          <td className={cn(td, "font-mono")}>
            {n.ipv4.length > 0 ? n.ipv4.join(", ") : n.saved_ipv4?.length ? `saved ${n.saved_ipv4.join(", ")}` : "—"}
          </td>
          <td className={cn(td, "font-mono")}>{n.gateway || "—"}</td>
          <td className={cn(td, "font-mono")}>{n.dns.join(", ") || "—"}</td>
          <td className={td}>{n.default_route ? <Badge variant="success">carries it</Badge> : "—"}</td>
        </tr>
      ))}
    </Table>
  );
}

export function SavedWifiView({ networks }: { networks: GatewaySavedWifi[] }) {
  if (networks.length === 0) return <p className="text-xs text-on-surface-muted">No saved Wi-Fi network.</p>;
  return (
    <ul className="flex flex-col gap-1 text-xs">
      {networks.map((n) => (
        <li key={`${n.ssid}-${n.iface ?? ""}`} className="flex items-center gap-2">
          <span className="font-medium text-on-surface">{n.ssid}</span>
          {n.active && <Badge variant="success">connected</Badge>}
          {n.hidden && <Badge variant="secondary">hidden</Badge>}
          {n.iface && <span className="text-on-surface-muted">on {n.iface}</span>}
          <span className="ml-auto text-on-surface-muted">priority {n.priority}</span>
        </li>
      ))}
    </ul>
  );
}

export function CloudDevicesView({ detail }: { detail: Pick<AdminGatewayDetail, "devices"> }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-on-surface-muted">
        <Cpu className="mr-1 inline size-3.5" />
        Instruments the cloud has under this gateway.
      </p>
      {detail.devices.length === 0 ? (
        <p className="text-xs text-on-surface-muted">None.</p>
      ) : (
        <Table head={["Name", "Driver", "Last value"]}>
          {detail.devices.map((d) => (
            <tr key={d.id} className="border-t border-border">
              <td className={cn(td, "font-medium")}>{d.name}</td>
              <td className={td}>{d.driver ?? "—"}</td>
              <td className={td}>{agoWords(d.last_seen)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  );
}

const STATE_TONE: Record<string, "success" | "warning" | "danger" | "secondary"> = {
  ok: "success",
  degraded: "warning",
  down: "danger",
};

function msAgo(ms: number | null): string {
  if (ms === null) return "never";
  if (ms < 1000) return "now";
  const s = Math.round(ms / 1000);
  return s < 120 ? `${s} s ago` : `${Math.round(s / 60)} min ago`;
}

export function DevicesView({ report }: { report: DevicesReport }) {
  if (report.devices.length === 0) {
    return <p className="text-xs text-on-surface-muted">The gateway polls no device.</p>;
  }
  return (
    <div className="flex flex-col gap-1">
      <Table head={["Device", "State", "Points", "Last good read", "Last error", "Latency"]}>
        {report.devices.map((d) => (
          <tr key={d.name} className="border-t border-border">
            <td className={cn(td, "font-medium")}>
              {d.name}
              <span className="block font-mono text-[11px] font-normal text-on-surface-muted">
                {d.driver} · {d.endpoint}
              </span>
            </td>
            <td className={td}>
              <Badge variant={STATE_TONE[d.state] ?? "secondary"}>{d.state}</Badge>
            </td>
            <td className={td}>
              {d.points}
              {d.points_bad > 0 && <span className="text-danger"> ({d.points_bad} bad)</span>}
            </td>
            <td className={td}>{msAgo(d.last_ok_ms_ago)}</td>
            <td className={cn(td, "max-w-[220px] break-words")}>
              {d.last_error ? `${d.last_error} (${msAgo(d.last_error_ms_ago)})` : "—"}
            </td>
            <td className={cn(td, "tabular-nums")}>
              {d.latency_ms.avg.toFixed(1)} ms
            </td>
          </tr>
        ))}
      </Table>
      {report.truncated ? (
        <p className="text-xs text-on-surface-muted">{report.truncated} more devices did not fit the answer.</p>
      ) : null}
    </div>
  );
}

const CHECK_TONE: Record<string, "success" | "warning" | "danger" | "secondary"> = {
  pass: "success",
  ok: "success",
  warn: "warning",
  fail: "danger",
  skip: "secondary",
};

export function HealthView({ report }: { report: HealthReport }) {
  return (
    <div className="flex flex-col gap-2">
      <p className="flex items-center gap-2 text-xs text-on-surface-muted">
        <Stethoscope className="size-3.5" />
        Overall <Badge variant={CHECK_TONE[report.overall] ?? "secondary"}>{report.overall}</Badge>
        in {report.duration_ms} ms
      </p>
      <Table head={["Check", "Result", "Value", "Detail"]}>
        {report.checks.map((c) => (
          <tr key={c.id} className="border-t border-border">
            <td className={td}>
              {c.label}
              <span className="block text-[11px] text-on-surface-muted">{c.group}</span>
            </td>
            <td className={td}>
              <Badge variant={CHECK_TONE[c.status] ?? "secondary"}>{c.status}</Badge>
            </td>
            <td className={cn(td, "tabular-nums")}>
              {c.value !== undefined ? `${c.value}${c.unit ? ` ${c.unit}` : ""}` : "—"}
            </td>
            <td className={cn(td, "max-w-[260px] break-words")}>{c.detail ?? "—"}</td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function PingView({ result }: { result: PingResult }) {
  const ms = (v: number | null) => (v === null ? "—" : `${v.toFixed(1)} ms`);
  return (
    <p className="flex flex-wrap items-center gap-2 text-xs text-on-surface">
      <Activity className="size-3.5" />
      <Badge variant={result.ok ? "success" : "danger"}>{result.ok ? "answered" : "no answer"}</Badge>
      {result.target} {result.ip && `(${result.ip})`} from {result.iface} by {result.method}: {result.received} of{" "}
      {result.sent} · min {ms(result.min_ms)} · avg {ms(result.avg_ms)} · max {ms(result.max_ms)}
      {result.detail && <span className="text-on-surface-muted">· {result.detail}</span>}
    </p>
  );
}

const LEVEL_TONE: Record<string, string> = { ERROR: "text-danger", WARN: "text-warning" };

export function LogTailView({ tail }: { tail: LogTail }) {
  if (tail.lines.length === 0) return <p className="text-xs text-on-surface-muted">No line matches.</p>;
  return (
    <div className="flex flex-col gap-1">
      <pre className="max-h-80 overflow-auto rounded-md bg-surface-muted p-2 font-mono text-[11px] leading-relaxed text-on-surface">
        {tail.lines.map((l, i) => (
          <div key={i}>
            <span className="text-on-surface-muted">{new Date(l.t).toISOString().slice(11, 23)}</span>{" "}
            <span className={LEVEL_TONE[l.level.toUpperCase()] ?? ""}>{l.level.toUpperCase().padEnd(5)}</span> {l.msg}
            {l.attrs && Object.keys(l.attrs).length > 0 && (
              <span className="text-on-surface-muted"> {JSON.stringify(l.attrs)}</span>
            )}
          </div>
        ))}
      </pre>
      {tail.dropped > 0 && (
        <p className="text-xs text-on-surface-muted">
          <FileText className="mr-1 inline size-3" />
          {tail.dropped} older matching lines left out.
        </p>
      )}
    </div>
  );
}

export function PointValueView({ result }: { result: PointReadResult }) {
  return (
    <p className="flex flex-wrap items-center gap-2 text-xs text-on-surface">
      <Gauge className="size-3.5" />
      <Badge variant={result.ok ? "success" : "danger"}>{result.quality}</Badge>
      {result.ok ? (
        <span className="font-mono text-sm">{JSON.stringify(result.value)}</span>
      ) : (
        <span className="text-danger">{result.error || "No value"}</span>
      )}
      {result.raw !== undefined && result.raw !== null && (
        <span className="font-mono text-on-surface-muted">raw {JSON.stringify(result.raw)}</span>
      )}
      <span className="text-on-surface-muted">{result.latency_ms} ms</span>
    </p>
  );
}

const OP_TONE: Record<AdminGatewayOp["status"], "success" | "warning" | "danger" | "secondary" | "info"> = {
  applied: "success",
  sent: "info",
  rejected: "warning",
  refused: "warning",
  error: "danger",
  timeout: "danger",
  failed: "danger",
};

export function OpHistoryView({ ops, now }: { ops: AdminGatewayOp[]; now?: Date }) {
  if (ops.length === 0) return <p className="text-xs text-on-surface-muted">No remote op yet.</p>;
  return (
    <Table head={["When", "Admin", "Request", "Result", "Took"]}>
      {ops.map((o) => {
        const answerError = o.answer?.opcode === OP_ERROR;
        return (
          <tr key={o.id} className="border-t border-border">
            <td className={td}>{agoWords(o.created_at, now)}</td>
            <td className={cn(td, "max-w-[160px] truncate")} title={o.actor_sub}>
              {o.actor_name ?? o.actor_email ?? o.actor_sub}
            </td>
            <td className={cn(td, "font-mono")}>{o.opcode_name || opLabel(o.opcode)}</td>
            <td className={td}>
              <Badge variant={answerError ? "warning" : OP_TONE[o.status]}>
                {answerError ? "ERROR" : o.status}
              </Badge>
              {(o.detail || answerError) && (
                <span className="block max-w-[260px] break-words text-[11px] text-on-surface-muted">
                  {answerError
                    ? String((o.answer?.body as { error?: unknown } | null)?.error ?? "")
                    : o.detail}
                </span>
              )}
            </td>
            <td className={cn(td, "tabular-nums")}>{o.took_ms !== null ? `${o.took_ms} ms` : "—"}</td>
          </tr>
        );
      })}
    </Table>
  );
}

export function FirmwareView({ detail }: { detail: Pick<AdminGatewayDetail, "firmware" | "firmware_line" | "capabilities"> }) {
  const birth = readBirth(detail.capabilities);
  return (
    <div className="flex flex-col gap-3">
      <dl className="grid grid-cols-2 gap-3">
        <Field label="Running">{detail.firmware ? `v${detail.firmware}` : "Not reported"}</Field>
        <Field label="Line">{lineLabel(detail.firmware_line)}</Field>
        <Field label="Host">{birth.host ?? "Not reported"}</Field>
        <Field label="Config schemas">{birth.configVersions.join(", ") || "—"}</Field>
      </dl>
      <p className="text-xs text-on-surface-muted">
        Releases and rollouts are in the firmware console.{" "}
        <Link to="/app/platform?tab=firmware" className="inline-flex items-center gap-1 text-brand hover:underline">
          <Router className="size-3.5" /> Open the firmware console <ExternalLink className="size-3" />
        </Link>
      </p>
    </div>
  );
}
